import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Briefcase, ArrowRight, Loader2, Search, Filter, Check, X, Download, Package, FileText, Car, Plane, Stamp, Globe, Plus, Edit3, Trash2, Save, DollarSign, Clock, Tag, Users, User, Languages, Receipt, Calculator, Printer, ShoppingBag, Home, Settings, LogOut, LayoutDashboard, ListChecks } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TranslateButton } from '../components/TranslateButton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import BreadcrumbNav from '../components/BreadcrumbNav';
import DashboardHeader from '../components/DashboardHeader';
import { DirhamPrice } from '../components/DirhamPrice';
import { usePerms } from '../permissions/PermissionsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Logo
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';

// بيانات الإمارات
const EMIRATES_DATA = {
  abudhabi: { 
    name_ar: 'أبوظبي', 
    name_en: 'Abu Dhabi', 
    color: '#c9a227',
    icon: '🏛️'
  },
  dubai: { 
    name_ar: 'دبي', 
    name_en: 'Dubai', 
    color: '#3b82f6',
    icon: '🌆'
  },
  sharjah: { 
    name_ar: 'الشارقة', 
    name_en: 'Sharjah', 
    color: '#10b981',
    icon: '🏰'
  },
  ajman: { 
    name_ar: 'عجمان', 
    name_en: 'Ajman', 
    color: '#f97316',
    icon: '🌅'
  },
  uaq: { 
    name_ar: 'أم القيوين', 
    name_en: 'Umm Al Quwain', 
    color: '#06b6d4',
    icon: '🏖️'
  },
  rak: { 
    name_ar: 'رأس الخيمة', 
    name_en: 'Ras Al Khaimah', 
    color: '#ef4444',
    icon: '⛰️'
  },
  fujairah: { 
    name_ar: 'الفجيرة', 
    name_en: 'Fujairah', 
    color: '#8b5cf6',
    icon: '🌊'
  }
};

// مكون إدارة باقات الإمارة
// Feb 2026 — `mode` prop:
//   • 'packages' → يفتح قائمة الباقات مباشرة (بدون Hub الأزرار الثلاثية)
//   • 'tracking' → يعرض المشتركين وطلبات الاشتراك فقط (مفصولين عن الباقات)
function EmiratePackagesView({ emirateId, onBack, language, activeSection, setActiveSection, mode = 'packages' }) {
  const { token } = useAuth();
  const emirate = EMIRATES_DATA[emirateId];
  const [packages, setPackages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // حقول الباقة الجديدة
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: '',
    price: '',
    duration: '',
    services: [],   // Feb 2026 — [{ ar, en }] (legacy strings coerced on load)
    is_active: true
  });

  // Feb 2026 — service being drafted before pressing "Add"
  const [newServiceAr, setNewServiceAr] = useState('');
  const [newServiceEn, setNewServiceEn] = useState('');

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/gov-packages/${emirateId}`,
        { headers: getHeaders() }
      );
      setPackages(res.data.data || []);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/gov-subscribers/${emirateId}`,
        { headers: getHeaders() }
      );
      setSubscribers(res.data.data || []);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/gov-requests/${emirateId}`,
        { headers: getHeaders() }
      );
      setRequests(res.data.data || []);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Scroll to top on section change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'packages') {
      fetchPackages();
    } else if (activeSection === 'subscribers') {
      fetchSubscribers();
    } else if (activeSection === 'requests') {
      fetchRequests();
    }
  }, [emirateId, activeSection]);

  const resetForm = () => {
    setFormData({
      name_ar: '',
      name_en: '',
      description_ar: '',
      description_en: '',
      price: '',
      duration: '',
      services: [],
      is_active: true
    });
    setNewServiceAr('');
    setNewServiceEn('');
  };

  const addService = () => {
    const ar = newServiceAr.trim();
    const en = newServiceEn.trim();
    if (!ar && !en) return;
    setFormData(prev => ({
      ...prev,
      // Feb 2026 — services are now {ar, en} pairs so admin can present the
      // package in either language to customers.
      services: [...prev.services, { ar, en }]
    }));
    setNewServiceAr('');
    setNewServiceEn('');
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.name_ar || !formData.price) {
      toast.error(language === 'ar' ? 'يرجى ملء الحقول المطلوبة' : 'Please fill required fields');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        emirate_id: emirateId,
        price: parseFloat(formData.price) || 0,
        duration: parseInt(formData.duration) || 0
      };
      
      if (selectedPackage) {
        await axios.put(
          `${BACKEND_URL}/api/admin/gov-packages/${selectedPackage.id}`,
          payload,
          { headers: getHeaders() }
        );
        toast.success(language === 'ar' ? 'تم تحديث الباقة بنجاح' : 'Package updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/gov-packages`,
          payload,
          { headers: getHeaders() }
        );
        toast.success(language === 'ar' ? 'تم إضافة الباقة بنجاح' : 'Package added successfully');
      }
      
      fetchPackages();
      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
      setSelectedPackage(null);
    } catch (err) {
      console.error('Error saving package:', err);
      toast.error(language === 'ar' ? 'فشل في حفظ الباقة' : 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه الباقة؟' : 'Are you sure you want to delete this package?')) {
      return;
    }
    
    setDeleting(packageId);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/gov-packages/${packageId}`,
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم حذف الباقة' : 'Package deleted');
      fetchPackages();
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في حذف الباقة' : 'Failed to delete package');
    } finally {
      setDeleting(null);
    }
  };

  const togglePackageStatus = async (pkg) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/gov-packages/${pkg.id}/toggle`,
        { is_active: !pkg.is_active },
        { headers: getHeaders() }
      );
      toast.success(pkg.is_active 
        ? (language === 'ar' ? 'تم إيقاف الباقة' : 'Package deactivated')
        : (language === 'ar' ? 'تم تفعيل الباقة' : 'Package activated')
      );
      fetchPackages();
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في تغيير الحالة' : 'Failed to toggle status');
    }
  };

  const openEditModal = (pkg) => {
    setSelectedPackage(pkg);
    // Feb 2026 — normalise legacy string services to {ar, en} pairs.
    const normalisedServices = (pkg.services || []).map(s =>
      typeof s === 'string'
        ? { ar: s, en: '' }
        : { ar: s.ar || s.name_ar || '', en: s.en || s.name_en || '' }
    );
    setFormData({
      name_ar: pkg.name_ar || '',
      name_en: pkg.name_en || '',
      description_ar: pkg.description_ar || '',
      description_en: pkg.description_en || '',
      price: pkg.price?.toString() || '',
      duration: pkg.duration?.toString() || '',
      services: normalisedServices,
      is_active: pkg.is_active !== false
    });
    setShowEditModal(true);
  };

  if (!emirate) {
    return <div className="text-white">إمارة غير موجودة</div>;
  }

  // Feb 2026 — Task C:
  // • mode='packages' → افتح قائمة الباقات مباشرة (بدون Hub الأزرار الثلاثية)
  // • mode='tracking' → أظهر Hub للمتابعة (المشتركين + الطلبات فقط)
  const effectiveActiveSection = (mode === 'packages' && activeSection === null)
    ? 'packages'
    : activeSection;

  // صفحة الأزرار الرئيسية للإمارة (Hub — يظهر فقط في mode='tracking')
  if (effectiveActiveSection === null) {
    return (
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl"
              style={{ backgroundColor: `${emirate.color}20`, border: `1px solid ${emirate.color}40` }}
            >
              {emirate.icon}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {language === 'ar' ? emirate.name_ar : emirate.name_en}
              </h2>
              <p className="text-sm text-[#808080]">
                {language === 'ar' ? 'متابعة المشتركين وطلبات الاشتراك' : 'Track Subscribers & Requests'}
              </p>
            </div>
          </div>
          <Button
            onClick={onBack}
            variant="outline"
            className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl"
          >
            <ArrowRight className="h-4 w-4 me-2" />
            {language === 'ar' ? 'رجوع' : 'Back'}
          </Button>
        </div>

        <p className="text-[#808080] mb-8 text-center">
          {language === 'ar' ? 'اختر القسم للإدارة' : 'Select section to manage'}
        </p>

        {/* Main Buttons Grid — Feb 2026: زر الباقات نُقل لمساره المستقل. هنا يظهر فقط
             المشتركين وطلبات الاشتراك (تحت "متابعة الاشتراكات والطلبات"). */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Feb 2026 — زر "الباقات" أُزيل من Hub المتابعة (mode='tracking').
              الباقات لها الآن مسار مستقل يبدأ من كرت "باقات تخليص الخدمات الحكومية"
              في الرئيسية → 7 إمارات → قائمة الباقات مباشرة (بدون هذا Hub). */}

          {/* زر المشتركين */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            whileHover={{ y: -5 }}
            onClick={() => setActiveSection('subscribers')}
            data-testid={`${emirateId}-subscribers-btn`}
            className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer text-center"
          >
            <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-emerald-500/20">
              <Users className="h-8 w-8 text-emerald-400" />
            </div>
            <h3 className="text-base font-bold text-white mb-1">
              {language === 'ar' ? 'المشتركين' : 'Subscribers'}
            </h3>
            <p className="text-xs text-[#71717A]">
              {language === 'ar' ? 'إدارة المشتركين الحاليين' : 'Manage Current Subscribers'}
            </p>
            {subscribers.length > 0 && (
              <span className="inline-block mt-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">
                {subscribers.length} {language === 'ar' ? 'مشترك' : 'subscribers'}
              </span>
            )}
          </motion.div>

          {/* زر طلبات المشتركين */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileHover={{ y: -5 }}
            onClick={() => setActiveSection('requests')}
            data-testid={`${emirateId}-requests-btn`}
            className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-blue-500/50 transition-all duration-300 cursor-pointer text-center"
          >
            <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-blue-500/20">
              <FileText className="h-8 w-8 text-blue-400" />
            </div>
            <h3 className="text-base font-bold text-white mb-1">
              {language === 'ar' ? 'طلبات المشتركين' : 'Subscription Requests'}
            </h3>
            <p className="text-xs text-[#71717A]">
              {language === 'ar' ? 'مراجعة طلبات الاشتراك' : 'Review Subscription Requests'}
            </p>
            {requests.length > 0 && (
              <span className="inline-block mt-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                {requests.length} {language === 'ar' ? 'طلب' : 'requests'}
              </span>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  // صفحة المشتركين
  if (effectiveActiveSection === 'subscribers') {
    return (
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setActiveSection(null)}
              variant="outline"
              className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl"
            >
              <ArrowRight className="h-4 w-4 me-2" />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center border border-emerald-500/30">
              <Users className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {language === 'ar' ? `مشتركين ${emirate.name_ar}` : `${emirate.name_en} Subscribers`}
              </h2>
              <p className="text-sm text-[#808080]">
                {language === 'ar' ? 'إدارة المشتركين الحاليين' : 'Manage current subscribers'}
              </p>
            </div>
          </div>
          <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-sm font-medium">
            {subscribers.length} {language === 'ar' ? 'مشترك' : 'subscribers'}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-emerald-500" />
            <p className="text-[#808080]">{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
          </div>
        ) : subscribers.length === 0 ? (
          <div className="text-center py-16 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
            <Users className="h-16 w-16 mx-auto mb-4 opacity-30 text-emerald-500" />
            <p className="text-lg text-[#808080]">
              {language === 'ar' ? 'لا يوجد مشتركين حالياً' : 'No subscribers yet'}
            </p>
            <p className="text-sm mt-2 text-[#606060]">
              {language === 'ar' ? 'سيظهر المشتركون هنا بعد الموافقة على طلباتهم' : 'Subscribers will appear here after their requests are approved'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {subscribers.map((sub) => (
              <div key={sub.id} className="p-4 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{sub.name || 'مشترك'}</h4>
                      <p className="text-sm text-[#808080]">{sub.email || sub.phone}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-white">{sub.package_name}</p>
                    <p className="text-xs text-[#808080]">{sub.subscribed_at}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // صفحة طلبات المشتركين
  if (effectiveActiveSection === 'requests') {
    return (
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setActiveSection(null)}
              variant="outline"
              className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl"
            >
              <ArrowRight className="h-4 w-4 me-2" />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center border border-blue-500/30">
              <FileText className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {language === 'ar' ? `طلبات اشتراك ${emirate.name_ar}` : `${emirate.name_en} Subscription Requests`}
              </h2>
              <p className="text-sm text-[#808080]">
                {language === 'ar' ? 'مراجعة طلبات الاشتراك الجديدة' : 'Review new subscription requests'}
              </p>
            </div>
          </div>
          <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-medium">
            {requests.length} {language === 'ar' ? 'طلب' : 'requests'}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
            <p className="text-[#808080]">{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
            <FileText className="h-16 w-16 mx-auto mb-4 opacity-30 text-blue-500" />
            <p className="text-lg text-[#808080]">
              {language === 'ar' ? 'لا توجد طلبات اشتراك جديدة' : 'No new subscription requests'}
            </p>
            <p className="text-sm mt-2 text-[#606060]">
              {language === 'ar' ? 'ستظهر الطلبات هنا عند تقديمها من العملاء' : 'Requests will appear here when submitted by customers'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => (
              <div key={req.id} className="p-4 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{req.name || 'طالب اشتراك'}</h4>
                      <p className="text-sm text-[#808080]">{req.email || req.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Check className="h-4 w-4 me-1" />
                      {language === 'ar' ? 'موافقة' : 'Approve'}
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-400 border-red-500/30 hover:bg-red-500/10">
                      <X className="h-4 w-4 me-1" />
                      {language === 'ar' ? 'رفض' : 'Reject'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // صفحة الباقات (الكود الأصلي)
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => {
              // Feb 2026 — في mode='packages' لا يوجد Hub وسيط: الرجوع مباشرة لشبكة الإمارات
              if (mode === 'packages') onBack();
              else setActiveSection(null);
            }}
            variant="outline"
            className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl"
          >
            <ArrowRight className="h-4 w-4 me-2" />
            {language === 'ar' ? 'رجوع' : 'Back'}
          </Button>
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${emirate.color}20`, border: `1px solid ${emirate.color}40` }}
          >
            <Package className="h-6 w-6" style={{ color: emirate.color }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {language === 'ar' ? `باقات ${emirate.name_ar}` : `${emirate.name_en} Packages`}
            </h2>
            <p className="text-sm text-[#808080]">
              {language === 'ar' ? 'إدارة باقات الخدمات الحكومية' : 'Manage government service packages'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              resetForm();
              setSelectedPackage(null);
              setShowAddModal(true);
            }}
            className="text-black rounded-xl"
            style={{ backgroundColor: emirate.color }}
            data-testid="add-package-btn"
          >
            <Plus className="h-4 w-4 me-2" />
            {language === 'ar' ? 'إضافة باقة' : 'Add Package'}
          </Button>
        </div>
      </div>

      {/* Packages Grid */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: emirate.color }} />
          <p className="text-[#808080]">{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      ) : packages.length === 0 ? (
        <div className="text-center py-16 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
          <Package className="h-16 w-16 mx-auto mb-4 opacity-30" style={{ color: emirate.color }} />
          <p className="text-lg text-[#808080]">
            {language === 'ar' ? 'لا توجد باقات بعد' : 'No packages yet'}
          </p>
          <p className="text-sm mt-2 text-[#606060]">
            {language === 'ar' ? 'اضغط على "إضافة باقة" لإنشاء باقة جديدة' : 'Click "Add Package" to create a new package'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-[#1a1a2e] rounded-2xl border transition-all ${
                pkg.is_active ? 'border-[#2a2a4e]' : 'border-red-500/30 opacity-60'
              }`}
            >
              {/* Package Header */}
              <div 
                className="p-4 rounded-t-2xl"
                style={{ backgroundColor: `${emirate.color}10`, borderBottom: `1px solid ${emirate.color}30` }}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-white">{pkg.name_ar}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    pkg.is_active 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {pkg.is_active 
                      ? (language === 'ar' ? 'مفعّل' : 'Active')
                      : (language === 'ar' ? 'متوقف' : 'Inactive')
                    }
                  </span>
                </div>
                {pkg.name_en && (
                  <p className="text-sm text-[#808080] mt-1">{pkg.name_en}</p>
                )}
              </div>

              {/* Package Body */}
              <div className="p-4">
                {/* Price & Duration */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" style={{ color: emirate.color }} />
                    <span className="text-white font-bold"><DirhamPrice value={pkg.price} decimals={2} size="sm" /></span>
                  </div>
                  {pkg.duration > 0 && (
                    <div className="flex items-center gap-2 text-[#808080]">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm">{pkg.duration} {language === 'ar' ? 'يوم' : 'days'}</span>
                    </div>
                  )}
                </div>

                {/* Description (bilingual) */}
                {(language === 'ar' ? pkg.description_ar : (pkg.description_en || pkg.description_ar)) && (
                  <p className="text-sm text-[#808080] mb-4 line-clamp-2">
                    {language === 'ar'
                      ? (pkg.description_ar || pkg.description_en)
                      : (pkg.description_en || pkg.description_ar)}
                  </p>
                )}

                {/* Also show package name in the other language on the card */}
                {language === 'ar' ? null : (pkg.name_en ? null : (
                  <p className="text-xs text-[#606060] -mt-3 mb-3">{pkg.name_ar}</p>
                ))}

                {/* Services */}
                {pkg.services && pkg.services.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-[#606060] mb-2 flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      {language === 'ar' ? 'الخدمات:' : 'Services:'}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {pkg.services.slice(0, 3).map((service, idx) => {
                        // Feb 2026 — services may be legacy strings or {ar, en} pairs.
                        const label = typeof service === 'string'
                          ? service
                          : (language === 'ar'
                              ? (service?.ar || service?.en || '')
                              : (service?.en || service?.ar || ''));
                        return (
                          <span key={idx} className="px-2 py-0.5 bg-[#0a0a0a] rounded text-xs text-[#808080]">
                            {label}
                          </span>
                        );
                      })}
                      {pkg.services.length > 3 && (
                        <span className="px-2 py-0.5 bg-[#0a0a0a] rounded text-xs text-[#808080]">
                          +{pkg.services.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-4 border-t border-[#2a2a4e]">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditModal(pkg)}
                    className="flex-1 border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
                  >
                    <Edit3 className="h-3 w-3 me-1" />
                    {language === 'ar' ? 'تعديل' : 'Edit'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => togglePackageStatus(pkg)}
                    className={`border-[#2a2a4e] ${pkg.is_active ? 'text-amber-400' : 'text-emerald-400'}`}
                  >
                    {pkg.is_active ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(pkg.id)}
                    disabled={deleting === pkg.id}
                    className="border-[#2a2a4e] text-red-400 hover:bg-red-500/10"
                  >
                    {deleting === pkg.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal || showEditModal} onOpenChange={() => {
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();
        setSelectedPackage(null);
      }}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" style={{ color: emirate.color }} />
              {selectedPackage 
                ? (language === 'ar' ? 'تعديل الباقة' : 'Edit Package')
                : (language === 'ar' ? 'إضافة باقة جديدة' : 'Add New Package')
              }
            </DialogTitle>
            <DialogDescription className="text-[#808080]">
              {language === 'ar'
                ? 'أدخل تفاصيل الباقة بالعربي والإنجليزي مع خدماتها المشمولة'
                : 'Enter package details in Arabic and English with included services'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name AR + translate FROM EN */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-[#808080]">
                  {language === 'ar' ? 'اسم الباقة (عربي) *' : 'Package Name (Arabic) *'}
                </label>
                <TranslateButton
                  sourceText={formData.name_en}
                  direction="en_to_ar"
                  onTranslated={(out) => setFormData((f) => ({ ...f, name_ar: out }))}
                  disabled={!formData.name_en.trim()}
                  testId="pkg-translate-name-ar"
                  size="sm"
                />
              </div>
              <Input
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                placeholder="مثال: باقة تأسيس الشركات"
                dir="rtl"
                data-testid="pkg-name-ar"
              />
            </div>

            {/* Name EN + translate FROM AR */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-[#808080]">
                  {language === 'ar' ? 'اسم الباقة (إنجليزي)' : 'Package Name (English)'}
                </label>
                <TranslateButton
                  sourceText={formData.name_ar}
                  direction="ar_to_en"
                  onTranslated={(out) => setFormData((f) => ({ ...f, name_en: out }))}
                  disabled={!formData.name_ar.trim()}
                  testId="pkg-translate-name-en"
                  size="sm"
                />
              </div>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                placeholder="e.g., Company Setup Package"
                dir="ltr"
                data-testid="pkg-name-en"
              />
            </div>

            {/* Price & Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#808080] block mb-1">
                  {language === 'ar' ? 'السعر (درهم) *' : 'Price *'}
                </label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm text-[#808080] block mb-1">
                  {language === 'ar' ? 'المدة (أيام)' : 'Duration (days)'}
                </label>
                <Input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Description AR + translate FROM EN */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-[#808080]">
                  {language === 'ar' ? 'الوصف (عربي)' : 'Description (Arabic)'}
                </label>
                <TranslateButton
                  sourceText={formData.description_en}
                  direction="en_to_ar"
                  onTranslated={(out) => setFormData((f) => ({ ...f, description_ar: out }))}
                  disabled={!formData.description_en.trim()}
                  testId="pkg-translate-desc-ar"
                  size="sm"
                />
              </div>
              <textarea
                value={formData.description_ar}
                onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white resize-none"
                rows={3}
                placeholder="وصف الباقة..."
                dir="rtl"
                data-testid="pkg-desc-ar"
              />
            </div>

            {/* Description EN + translate FROM AR (Feb 2026 — new field) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-[#808080]">
                  {language === 'ar' ? 'الوصف (إنجليزي)' : 'Description (English)'}
                </label>
                <TranslateButton
                  sourceText={formData.description_ar}
                  direction="ar_to_en"
                  onTranslated={(out) => setFormData((f) => ({ ...f, description_en: out }))}
                  disabled={!formData.description_ar.trim()}
                  testId="pkg-translate-desc-en"
                  size="sm"
                />
              </div>
              <textarea
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white resize-none"
                rows={3}
                placeholder="Package description..."
                dir="ltr"
                data-testid="pkg-desc-en"
              />
            </div>

            {/* Services — bilingual pair with translate + list */}
            <div>
              <label className="text-sm text-[#808080] block mb-2">
                {language === 'ar' ? 'الخدمات المشمولة' : 'Included Services'}
              </label>

              {/* Draft row */}
              <div className="grid grid-cols-1 gap-2 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl p-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-white/50">{language === 'ar' ? 'عربي' : 'AR'}</span>
                      <TranslateButton
                        sourceText={newServiceEn}
                        direction="en_to_ar"
                        onTranslated={(out) => setNewServiceAr(out)}
                        disabled={!newServiceEn.trim()}
                        testId="pkg-service-translate-ar"
                        size="sm"
                      />
                    </div>
                    <Input
                      value={newServiceAr}
                      onChange={(e) => setNewServiceAr(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                      className="bg-black border-[#2a2a4e] text-white h-9"
                      placeholder={language === 'ar' ? 'اسم الخدمة بالعربية' : 'Service (AR)'}
                      dir="rtl"
                      data-testid="pkg-new-service-ar"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-white/50">{language === 'ar' ? 'إنجليزي' : 'EN'}</span>
                      <TranslateButton
                        sourceText={newServiceAr}
                        direction="ar_to_en"
                        onTranslated={(out) => setNewServiceEn(out)}
                        disabled={!newServiceAr.trim()}
                        testId="pkg-service-translate-en"
                        size="sm"
                      />
                    </div>
                    <Input
                      value={newServiceEn}
                      onChange={(e) => setNewServiceEn(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                      className="bg-black border-[#2a2a4e] text-white h-9"
                      placeholder="Service (EN)"
                      dir="ltr"
                      data-testid="pkg-new-service-en"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={addService}
                  className="text-black w-full"
                  style={{ backgroundColor: emirate.color }}
                  data-testid="pkg-add-service-btn"
                >
                  <Plus className="h-4 w-4 me-1" />
                  {language === 'ar' ? 'إضافة الخدمة للباقة' : 'Add service to package'}
                </Button>
              </div>

              {/* Existing services list */}
              {formData.services.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-[#0a0a0a] rounded-xl mt-2">
                  {formData.services.map((service, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      style={{ backgroundColor: `${emirate.color}20`, color: emirate.color }}
                      data-testid={`pkg-service-item-${idx}`}
                    >
                      <span dir="rtl">{service.ar || service.en || String(service)}</span>
                      {service.en && service.ar && <span className="text-white/40 text-[11px]" dir="ltr">/ {service.en}</span>}
                      <button onClick={() => removeService(idx)} className="hover:text-red-400">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <label htmlFor="is_active" className="text-sm text-[#808080]">
                {language === 'ar' ? 'الباقة مفعّلة' : 'Package is active'}
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
                setSelectedPackage(null);
              }}
              className="border-[#2a2a4e] text-white"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-black"
              style={{ backgroundColor: emirate.color }}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin me-2" />
              ) : (
                <Save className="h-4 w-4 me-2" />
              )}
              {language === 'ar' ? 'حفظ' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ControlCloudGM() {
  const navigate = useNavigate();
  const { canViewRoute } = usePerms();
  const { language } = useLanguage();
  const { token, user, logout } = useAuth();

  // activeView synced with the URL (?view=...&section=...) so the browser
  // Back button works and sub-pages can deep-link
  // (e.g. /control/cloudgm?view=printing-documents).
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = searchParams.get('view') || 'main';
  const emirateSection = searchParams.get('section') || null;
  // Feb 2026 — Task C: mode لصفحات الإمارات
  //   • 'packages' (افتراضي) → قائمة الباقات مباشرة
  //   • 'tracking' → Hub المشتركين وطلبات الاشتراك
  const emirateMode = searchParams.get('mode') || 'packages';
  const setActiveView = (v, extra = {}) => {
    if (!v || v === 'main') setSearchParams({});
    else setSearchParams({ view: v, ...extra });
  };
  const setEmirateSection = (s) => {
    const next = { view: activeView };
    if (emirateMode && emirateMode !== 'packages') next.mode = emirateMode;
    if (s) next.section = s;
    setSearchParams(next);
  };
  
  // RAKEZ Activities State
  const [rakezActivities, setRakezActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [licenseTypeFilter, setLicenseTypeFilter] = useState('all');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [availableFilters, setAvailableFilters] = useState({ license_types: [], zones: [] });
  const [togglingActivity, setTogglingActivity] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 50;

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchRakezActivities = async (page = 1) => {
    setLoadingActivities(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (licenseTypeFilter !== 'all') params.append('license_type', licenseTypeFilter);
      if (zoneFilter !== 'all') params.append('zone', zoneFilter);
      params.append('page', page);
      params.append('limit', ITEMS_PER_PAGE);
      
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/rakez-activities?${params.toString()}`,
        { headers: getHeaders() }
      );
      setRakezActivities(res.data.data || []);
      setAvailableFilters(res.data.filters || { license_types: [], zones: [] });
      setTotalPages(res.data.total_pages || 1);
      setTotalCount(res.data.total || 0);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching RAKEZ activities:', err);
      toast.error(language === 'ar' ? 'فشل في جلب الأنشطة' : 'Failed to fetch activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const toggleActivity = async (activityId) => {
    setTogglingActivity(activityId);
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/admin/rakez-activities/${activityId}/toggle`,
        {},
        { headers: getHeaders() }
      );
      
      // تحديث الحالة محلياً
      setRakezActivities(prev => prev.map(act => 
        act.id === activityId ? { ...act, is_active: res.data.is_active } : act
      ));
      
      toast.success(res.data.is_active 
        ? (language === 'ar' ? 'تم تفعيل النشاط' : 'Activity activated')
        : (language === 'ar' ? 'تم إلغاء تفعيل النشاط' : 'Activity deactivated')
      );
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في تحديث النشاط' : 'Failed to update activity');
    } finally {
      setTogglingActivity(null);
    }
  };

  const seedActivities = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/rakez-activities/seed`,
        {},
        { headers: getHeaders() }
      );
      toast.success(res.data.message);
      fetchRakezActivities();
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في تحميل الأنشطة' : 'Failed to seed activities');
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    if (activeView === 'rakez-rak') {
      fetchRakezActivities(1);
      setCurrentPage(1);
    }
  }, [activeView, statusFilter, licenseTypeFilter, zoneFilter]);

  // Debounced search
  useEffect(() => {
    if (activeView === 'rakez-rak') {
      const timer = setTimeout(() => {
        fetchRakezActivities(1);
        setCurrentPage(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  const getBackDestination = () => {
    if (activeView === 'main') return '/dashboard?view=platform-control&sub=fzlc';  // Go back to platform control FZLC selection
    // الأنشطة التجارية
    if (activeView === 'ded-rak' || activeView === 'rakez-rak') return 'activities';
    // طلبات الطباعة والمستندات
    if (['visa-id', 'document-auth', 'traffic-services', 'tourist-visa'].includes(activeView)) return 'printing-documents';
    // الإمارات في باقات الخدمات الحكومية / متابعة الاشتراكات
    if (activeView.startsWith('emirate-')) {
      return emirateMode === 'tracking' ? 'gov-services-tracking' : 'gov-services-packages';
    }
    // طلبات الخدمة الفرعية
    if (activeView.startsWith('sr-')) return 'service-requests';
    // أي view آخر يعود للرئيسية
    return 'main';
  };

  // Dynamic breadcrumb items — includes intermediate parent crumbs so deep
  // pages show the full clickable path.
  const getBreadcrumbItems = () => {
    // المسار: التحكم والمعلومات للمنصات > CLOUDGM FZ LLC (للتحكم) > التحكم والمعلومات (منصة العملاء)
    const basePath = [
      { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
      { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
      { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
    ];
    
    if (activeView === 'main') {
      return [...basePath, { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: null }];
    }
    
    const viewLabels = {
      'company-setup': { ar: 'تأسيس الشركات', en: 'Company Setup' },
      'gov-services-packages': { ar: 'باقات تخليص الخدمات الحكومية', en: 'Government Services Packages' },
      'gov-services-tracking': { ar: 'متابعة الاشتراكات والطلبات', en: 'Subscribers & Requests Tracking' },
      'activities': { ar: 'الأنشطة التجارية', en: 'Business Activities' },
      'printing-documents': { ar: 'الطباعة وتخليص المستندات', en: 'Printing & Documents' },
      'service-requests': { ar: 'طلبات الخدمة', en: 'Service Requests' },
      'ded-rak': { ar: 'أنشطة دائرة التنمية الاقتصادية', en: 'DED Activities' },
      'rakez-rak': { ar: 'أنشطة المنطقة الحرة RAKEZ', en: 'RAKEZ Activities' },
      'visa-id': { ar: 'تقديم طلب التأشيرة والهوية', en: 'Visa & Emirates ID' },
      'document-auth': { ar: 'توثيق المستندات', en: 'Document Authentication' },
      'traffic-services': { ar: 'خدمات المرور', en: 'Traffic Services' },
      // Feb 2026 — 'tourist-visa' removed from printing-docs sub-navigation
      // (it's still reachable directly from the parent Customers-Control page).
      'legal-translation': { ar: 'الترجمة القانونية', en: 'Legal Translation' },
    };

    const crumbs = [
      ...basePath,
      { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
    ];

    // صفحات الإمارات: التحكم → (باقات الخدمات | متابعة الاشتراكات) → الإمارة
    if (activeView.startsWith('emirate-')) {
      const em = EMIRATES_DATA[activeView.replace('emirate-', '')];
      const parentKey = emirateMode === 'tracking' ? 'gov-services-tracking' : 'gov-services-packages';
      const parentPath = emirateMode === 'tracking'
        ? '/control/cloudgm?view=gov-services-tracking'
        : '/control/cloudgm?view=gov-services-packages';
      crumbs.push({
        label_ar: viewLabels[parentKey].ar,
        label_en: viewLabels[parentKey].en,
        path: parentPath,
      });
      crumbs.push({ label_ar: em?.name_ar || activeView, label_en: em?.name_en || activeView, path: null });
      return crumbs;
    }

    // مستوى وسيط للصفحات العميقة (الأب القابل للنقر)
    const parentOf = {
      'ded-rak': 'activities',
      'rakez-rak': 'activities',
      'visa-id': 'printing-documents',
      'document-auth': 'printing-documents',
      'traffic-services': 'printing-documents',
    };
    const parentView = parentOf[activeView];
    if (parentView && viewLabels[parentView]) {
      crumbs.push({
        label_ar: viewLabels[parentView].ar,
        label_en: viewLabels[parentView].en,
        path: `/control/cloudgm?view=${parentView}`,
      });
    }

    const label = viewLabels[activeView] || { ar: activeView, en: activeView };
    crumbs.push({ label_ar: label.ar, label_en: label.en, path: null });
    return crumbs;
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Unified Header */}
      <DashboardHeader 
        onBack={() => {
          // داخل قسم فرعي لإمارة → الرجوع لأزرار الإمارة أولاً
          if (activeView.startsWith('emirate-') && emirateSection) {
            setEmirateSection(null);
            return;
          }
          const dest = getBackDestination();
          if (dest.startsWith('/')) {
            navigate(dest);
          } else {
            setActiveView(dest);
          }
        }}
      />

      {/* Breadcrumb with border */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={getBreadcrumbItems()} />
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Main View */}
        {activeView === 'main' && (
          <>
            {/* Page Title */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <h1 className="text-2xl font-bold text-white">
                {language === 'ar' ? 'التحكم والمعلومات بمنصة العملاء' : 'Customers Platform Control & Info'}
              </h1>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* تأسيس الشركات */}
            {canViewRoute('/control/company-setup') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate('/control/company-setup')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-indigo-500/50 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-business-activities"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-indigo-500/10 rounded-md">
                <span className="text-[10px] font-mono text-indigo-400">CGM-C01</span>
              </div>
              <div className="w-14 h-14 bg-indigo-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-indigo-500/20">
                <Briefcase className="h-8 w-8 text-indigo-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'تأسيس الشركات' : 'Company Setup'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'إدارة أنشطة تأسيس الشركات' : 'Manage company setup activities'}
              </p>
            </motion.div>
            )}

            {/* Feb 2026 — كرت "التحكم بالقوائم المنسدلة للفواتير" حُذف نهائياً (طلب المستخدم).
                استُبدل بكرت جديد "القوائم المنسدلة للتحكم بمنصة العملاء" (يفتح صفحة
                فيها إدارة رئيسية/فرعية لمسمى الخدمة اليدوي وغيرها من القوائم المستقبلية) */}
            {canViewRoute('/control/cloudgm/customer-dropdowns') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate('/control/cloudgm/customer-dropdowns')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-customer-dropdowns"
            >
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-cyan-500/10 rounded-md">
                <span className="text-[10px] font-mono text-cyan-400">CGM-C02</span>
              </div>
              <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-cyan-500/20">
                <ListChecks className="h-8 w-8 text-cyan-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'القوائم المنسدلة للتحكم بمنصة العملاء' : 'Customer Platform Dropdown Lists'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'إدارة مسميات الخدمات المستخدمة في إدخال الطلبات اليدوية' : 'Manage service names used in manual customer requests'}
              </p>
            </motion.div>
            )}

            {/* Jun 2026 — سجلات محركات البحث (منصة العملاء تكتبها في asb606_admin.site_search_logs) */}
            {canViewRoute('/control/cloudgm/search-logs') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate('/control/cloudgm/search-logs')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/50 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-search-logs"
            >
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-[#d4af37]/10 rounded-md">
                <span className="text-[10px] font-mono text-[#d4af37]">CGM-C10</span>
              </div>
              <div className="w-14 h-14 bg-[#d4af37]/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-[#d4af37]/20">
                <Search className="h-8 w-8 text-[#d4af37]" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'سجلات محركات البحث' : 'Search Engine Logs'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'كل ما يبحث عنه العملاء والزوار في منصة العملاء — للدراسة والتحليل' : 'Everything customers & guests search on the customer platform — for study'}
              </p>
            </motion.div>
            )}

            {/* قائمة الأوراق المطلوبة — moved to: لوحة التحكم → التحكم والمعلومات للمنصات → ASB606 ADMIN → الأوراق المطلوبة (iter-73) */}

            {/* كرت "التحكم بالخدمات" أُزيل — كانت وجهته (services-control) صفحة فارغة،
                وكل محتوياته السابقة أصبحت كروتاً مباشرة في هذه الصفحة */}

            {/* ============== نهاية section 1 من main view - بداية section 2 ============== */}
            {/* باقات تخليص الخدمات الحكومية - في main view */}
            {canViewRoute('/control/cloudgm?tab=gov-services-packages') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ y: -5 }}
              onClick={() => setActiveView('gov-services-packages')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-amber-500/50 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-gov-packages"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-amber-500/10 rounded-md">
                <span className="text-[10px] font-mono text-amber-400">CGM-C05</span>
              </div>
              <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-amber-500/20">
                <Package className="h-8 w-8 text-amber-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'باقات تخليص الخدمات الحكومية' : 'Government Services Packages'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'إدارة باقات الخدمات الحكومية' : 'Manage government service packages'}
              </p>
            </motion.div>
            )}

            {/* Feb 2026 — Task C removed (iter-272): كرت "متابعة الاشتراكات والطلبات"
                حُذف بالكامل بطلب المستخدم. الوصول للمشتركين يتم عبر باقات الخدمات مباشرة. */}

              {/* التحكم بطلبات الطباعة وتخليص المستندات */}
            {canViewRoute('/control/cloudgm?tab=printing-documents') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ y: -5 }}
              onClick={() => setActiveView('printing-documents')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-printing"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-cyan-500/10 rounded-md">
                <span className="text-[10px] font-mono text-cyan-400">CGM-C06</span>
              </div>
              <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-cyan-500/20">
                <Printer className="h-8 w-8 text-cyan-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'الطباعة وتخليص المستندات' : 'Printing & Document Clearance'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'إدارة طلبات الطباعة والتخليص' : 'Manage printing and clearance requests'}
              </p>
            </motion.div>
            )}

              {/* تأشيرة سياحية لجميع الدول والجنسيات */}
            {canViewRoute('/visa-requirements') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate('/visa-requirements')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-tourist-visa"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-purple-500/10 rounded-md">
                <span className="text-[10px] font-mono text-purple-400">CGM-C07</span>
              </div>
              <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-purple-500/20">
                <Globe className="h-8 w-8 text-purple-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'التأشيرة السياحية' : 'Tourist Visa'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'إدارة متطلبات جميع الجنسيات' : 'Manage all nationalities requirements'}
              </p>
            </motion.div>
            )}

              {/* خدمات الترجمة القانونية */}
            {canViewRoute('/control/cloudgm/legal-translation') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate('/control/cloudgm/legal-translation')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-rose-500/50 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-legal-trans"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-rose-500/10 rounded-md">
                <span className="text-[10px] font-mono text-rose-400">CGM-C08</span>
              </div>
              <div className="w-14 h-14 bg-rose-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-rose-500/20">
                <Languages className="h-8 w-8 text-rose-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'الترجمة القانونية' : 'Legal Translation'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'خدمات الترجمة المعتمدة' : 'Certified translation services'}
              </p>
            </motion.div>
            )}

              {/* iter-272 — الخدمات الضريبية (زر جديد بطلب المستخدم) */}
            {canViewRoute('/control/cloudgm/tax-services') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate('/control/cloudgm/tax-services')}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-amber-500/50 transition-all duration-300 cursor-pointer text-center"
              data-testid="ctrl-tax-services"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-amber-500/10 rounded-md">
                <span className="text-[10px] font-mono text-amber-400">CGM-C09</span>
              </div>
              <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-amber-500/20">
                <Calculator className="h-8 w-8 text-amber-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-1">
                {language === 'ar' ? 'الخدمات الضريبية' : 'Tax Services'}
              </h3>
              <p className="text-xs text-[#71717A]">
                {language === 'ar' ? 'إدارة خدمات الضرائب والأوراق المطلوبة' : 'Manage tax services & required docs'}
              </p>
            </motion.div>
            )}
            </div>
          </>
        )}

        {/* Activities Control View */}
        {activeView === 'activities' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-indigo-500" />
              {language === 'ar' ? 'التحكم بالأنشطة التجارية' : 'Business Activities Control'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* DED RAK */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                onClick={() => setActiveView('ded-rak')}
                className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer text-center"
                data-testid="ctrl-ded-rak"
              >
                <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-emerald-500/20">
                  <Building2 className="h-8 w-8 text-emerald-400" />
                </div>
                <h3 className="text-base font-bold text-white mb-1">
                  {language === 'ar' ? 'الدائرة الاقتصادية راس الخيمة' : 'DED Ras Al Khaimah'}
                </h3>
                <p className="text-xs text-[#71717A]">
                  {language === 'ar' ? 'أنشطة DED RAK' : 'DED RAK Activities'}
                </p>
              </motion.div>

              {/* RAKEZ RAK */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                whileHover={{ y: -5 }}
                onClick={() => setActiveView('rakez-rak')}
                className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-blue-500/50 transition-all duration-300 cursor-pointer text-center"
                data-testid="ctrl-rakez-rak"
              >
                <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-blue-500/20">
                  <Building2 className="h-8 w-8 text-blue-400" />
                </div>
                <h3 className="text-base font-bold text-white mb-1">
                  {language === 'ar' ? 'راكز راس الخيمة' : 'RAKEZ Ras Al Khaimah'}
                </h3>
                <p className="text-xs text-[#71717A]">
                  {language === 'ar' ? 'أنشطة RAKEZ RAK' : 'RAKEZ RAK Activities'}
                </p>
              </motion.div>
            </div>
          </div>
        )}

        {/* DED RAK Activities View */}
        {activeView === 'ded-rak' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-emerald-500" />
              {language === 'ar' ? 'أنشطة الدائرة الاقتصادية - راس الخيمة' : 'DED Activities - Ras Al Khaimah'}
            </h2>

            <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
              <Building2 className="h-16 w-16 mx-auto mb-4 opacity-30 text-emerald-500" />
              <p className="text-lg">{language === 'ar' ? 'لم يتم تحميل الأنشطة بعد' : 'Activities not loaded yet'}</p>
              <p className="text-sm mt-2 text-[#606060]">
                {language === 'ar' ? 'يرجى تحميل ملف Excel للأنشطة' : 'Please upload Excel file for activities'}
              </p>
            </div>
          </div>
        )}

        {/* RAKEZ RAK Activities View */}
        {activeView === 'rakez-rak' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Building2 className="h-6 w-6 text-blue-500" />
                {language === 'ar' ? 'أنشطة راكز - راس الخيمة' : 'RAKEZ Activities - Ras Al Khaimah'}
              </h2>
              
              {rakezActivities.length === 0 && !loadingActivities && (
                <Button
                  onClick={seedActivities}
                  disabled={seeding}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                >
                  {seeding ? (
                    <Loader2 className="h-4 w-4 animate-spin me-2" />
                  ) : (
                    <Download className="h-4 w-4 me-2" />
                  )}
                  {language === 'ar' ? 'تحميل الأنشطة' : 'Load Activities'}
                </Button>
              )}
            </div>

            {/* Search and Filters */}
            <div className="bg-[#1a1a2e] rounded-2xl p-4 border border-[#2a2a4e] mb-6">
              <div className="flex flex-wrap gap-4">
                {/* Search */}
                <div className="flex-1 min-w-[250px]">
                  <div className="relative">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#606060]" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={language === 'ar' ? 'بحث عن نشاط...' : 'Search activities...'}
                      className="bg-[#0a0a0a] border-[#2a2a4e] text-white pr-10 rounded-xl"
                    />
                  </div>
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-[#0a0a0a] border border-[#2a2a4e] text-white px-3 py-2 rounded-xl text-sm min-w-[140px]"
                >
                  <option value="all">{language === 'ar' ? 'كل الحالات' : 'All Status'}</option>
                  <option value="Active">{language === 'ar' ? 'نشط' : 'Active'}</option>
                  <option value="Inactive">{language === 'ar' ? 'غير نشط' : 'Inactive'}</option>
                </select>

                {/* License Type Filter */}
                <select
                  value={licenseTypeFilter}
                  onChange={(e) => setLicenseTypeFilter(e.target.value)}
                  className="bg-[#0a0a0a] border border-[#2a2a4e] text-white px-3 py-2 rounded-xl text-sm min-w-[140px]"
                >
                  <option value="all">{language === 'ar' ? 'كل الرخص' : 'All Licenses'}</option>
                  {availableFilters.license_types.map(lt => (
                    <option key={lt} value={lt}>{lt}</option>
                  ))}
                </select>

                {/* Zone Filter */}
                <select
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                  className="bg-[#0a0a0a] border border-[#2a2a4e] text-white px-3 py-2 rounded-xl text-sm min-w-[140px]"
                >
                  <option value="all">{language === 'ar' ? 'كل المناطق' : 'All Zones'}</option>
                  {availableFilters.zones.map(z => (
                    <option key={z} value={z}>{z}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Activities Count */}
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[#808080] text-sm">
                {language === 'ar' 
                  ? `إجمالي الأنشطة: ${totalCount} | الصفحة ${currentPage} من ${totalPages}` 
                  : `Total Activities: ${totalCount} | Page ${currentPage} of ${totalPages}`}
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => fetchRakezActivities(currentPage - 1)}
                    disabled={currentPage <= 1 || loadingActivities}
                    variant="outline"
                    size="sm"
                    className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
                  >
                    {language === 'ar' ? 'السابق' : 'Previous'}
                  </Button>
                  <span className="text-white text-sm px-3">{currentPage}</span>
                  <Button
                    onClick={() => fetchRakezActivities(currentPage + 1)}
                    disabled={currentPage >= totalPages || loadingActivities}
                    variant="outline"
                    size="sm"
                    className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
                  >
                    {language === 'ar' ? 'التالي' : 'Next'}
                  </Button>
                </div>
              )}
            </div>

            {/* Activities List */}
            {loadingActivities ? (
              <div className="flex items-center justify-center py-16 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : rakezActivities.length === 0 ? (
              <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
                <Building2 className="h-16 w-16 mx-auto mb-4 opacity-30 text-blue-500" />
                <p className="text-lg">{language === 'ar' ? 'لا توجد أنشطة' : 'No activities found'}</p>
                <p className="text-sm mt-2 text-[#606060]">
                  {searchQuery 
                    ? (language === 'ar' ? 'جرب تغيير معايير البحث' : 'Try changing search criteria')
                    : (language === 'ar' ? 'اضغط على "تحميل الأنشطة" لإضافة البيانات' : 'Click "Load Activities" to add data')
                  }
                </p>
              </div>
            ) : (
              <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#0a0a0a] border-b border-[#2a2a4e]">
                      <tr>
                        <th className="text-right py-3 px-4 text-sm font-medium text-[#808080]">
                          {language === 'ar' ? 'فعال' : 'Active'}
                        </th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-[#808080]">
                          {language === 'ar' ? 'كود النشاط' : 'Activity Code'}
                        </th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-[#808080]">
                          {language === 'ar' ? 'اسم النشاط' : 'Activity Name'}
                        </th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-[#808080]">
                          {language === 'ar' ? 'نوع الرخصة' : 'License Type'}
                        </th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-[#808080]">
                          {language === 'ar' ? 'المنطقة' : 'Zone'}
                        </th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-[#808080]">
                          {language === 'ar' ? 'القطاع' : 'Sector'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2a2a4e]">
                      {rakezActivities.map((activity) => (
                        <tr key={activity.id} className="hover:bg-[#0a0a0a]/50 transition-colors">
                          <td className="py-3 px-4">
                            <button
                              onClick={() => toggleActivity(activity.id)}
                              disabled={togglingActivity === activity.id}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                activity.is_active 
                                  ? 'bg-emerald-500/20 text-emerald-500 hover:bg-emerald-500/30' 
                                  : 'bg-[#2a2a4e] text-[#606060] hover:bg-[#3a3a5e]'
                              }`}
                            >
                              {togglingActivity === activity.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : activity.is_active ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-[#c9a227] font-mono">
                              {activity.activity_code}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-white font-medium text-sm">
                                {language === 'ar' ? activity.activity_name_ar : activity.activity_name}
                              </p>
                              <p className="text-xs text-[#606060] mt-0.5">
                                {language === 'ar' ? activity.activity_name : activity.activity_name_ar}
                              </p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-1 rounded-lg ${
                              activity.license_type === 'Commercial' ? 'bg-blue-500/20 text-blue-400' :
                              activity.license_type === 'Industrial' ? 'bg-orange-500/20 text-orange-400' :
                              activity.license_type === 'Services' ? 'bg-emerald-500/20 text-emerald-400' :
                              activity.license_type === 'Professional' ? 'bg-purple-500/20 text-purple-400' :
                              activity.license_type === 'Educational' ? 'bg-cyan-500/20 text-cyan-400' :
                              activity.license_type === 'Media' ? 'bg-pink-500/20 text-pink-400' :
                              'bg-[#2a2a4e] text-[#808080]'
                            }`}>
                              {language === 'ar' ? activity.license_type_ar : activity.license_type}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-[#808080]">
                              {activity.zone}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-[#808080]">
                              {activity.sector}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============== باقات تخليص الخدمات الحكومية ============== */}
        {activeView === 'gov-services-packages' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Package className="h-6 w-6 text-amber-500" />
              {language === 'ar' ? 'باقات تخليص الخدمات الحكومية' : 'Government Services Packages'}
            </h2>
            <p className="text-[#808080] mb-8">
              {language === 'ar' ? 'اختر الإمارة لإدارة باقات الخدمات الحكومية' : 'Select an emirate to manage government service packages'}
            </p>

            {/* شبكة الإمارات السبع */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {/* أبوظبي */}
              <div
                onClick={() => setActiveView('emirate-abudhabi')}
                className="group bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e] hover:border-[#c9a227] transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-[#c9a227]/10"
                data-testid="emirate-abudhabi"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#c9a227]/20 to-[#c9a227]/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform border border-[#c9a227]/30">
                    <span className="text-3xl">🏛️</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {language === 'ar' ? 'أبوظبي' : 'Abu Dhabi'}
                  </h3>
                  <p className="text-xs text-[#808080]">
                    {language === 'ar' ? 'العاصمة' : 'Capital'}
                  </p>
                </div>
              </div>

              {/* دبي */}
              <div
                onClick={() => setActiveView('emirate-dubai')}
                className="group bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e] hover:border-blue-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/10"
                data-testid="emirate-dubai"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform border border-blue-500/30">
                    <span className="text-3xl">🌆</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {language === 'ar' ? 'دبي' : 'Dubai'}
                  </h3>
                  <p className="text-xs text-[#808080]">
                    {language === 'ar' ? 'مدينة الأعمال' : 'Business Hub'}
                  </p>
                </div>
              </div>

              {/* الشارقة */}
              <div
                onClick={() => setActiveView('emirate-sharjah')}
                className="group bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e] hover:border-emerald-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-500/10"
                data-testid="emirate-sharjah"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform border border-emerald-500/30">
                    <span className="text-3xl">🏰</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {language === 'ar' ? 'الشارقة' : 'Sharjah'}
                  </h3>
                  <p className="text-xs text-[#808080]">
                    {language === 'ar' ? 'عاصمة الثقافة' : 'Cultural Capital'}
                  </p>
                </div>
              </div>

              {/* عجمان */}
              <div
                onClick={() => setActiveView('emirate-ajman')}
                className="group bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e] hover:border-orange-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-orange-500/10"
                data-testid="emirate-ajman"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500/20 to-orange-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform border border-orange-500/30">
                    <span className="text-3xl">🌅</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {language === 'ar' ? 'عجمان' : 'Ajman'}
                  </h3>
                  <p className="text-xs text-[#808080]">
                    {language === 'ar' ? 'الإمارة الصغيرة' : 'The Small Emirate'}
                  </p>
                </div>
              </div>

              {/* أم القيوين */}
              <div
                onClick={() => setActiveView('emirate-uaq')}
                className="group bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e] hover:border-cyan-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-cyan-500/10"
                data-testid="emirate-uaq"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform border border-cyan-500/30">
                    <span className="text-3xl">🏖️</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {language === 'ar' ? 'أم القيوين' : 'Umm Al Quwain'}
                  </h3>
                  <p className="text-xs text-[#808080]">
                    {language === 'ar' ? 'جوهرة الساحل' : 'Coastal Gem'}
                  </p>
                </div>
              </div>

              {/* رأس الخيمة */}
              <div
                onClick={() => setActiveView('emirate-rak')}
                className="group bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e] hover:border-red-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-red-500/10"
                data-testid="emirate-rak"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-red-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform border border-red-500/30">
                    <span className="text-3xl">⛰️</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {language === 'ar' ? 'رأس الخيمة' : 'Ras Al Khaimah'}
                  </h3>
                  <p className="text-xs text-[#808080]">
                    {language === 'ar' ? 'إمارة الجبال' : 'Mountain Emirate'}
                  </p>
                </div>
              </div>

              {/* الفجيرة */}
              <div
                onClick={() => setActiveView('emirate-fujairah')}
                className="group bg-[#1a1a2e] rounded-2xl p-6 border border-[#2a2a4e] hover:border-purple-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-500/10"
                data-testid="emirate-fujairah"
              >
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform border border-purple-500/30">
                    <span className="text-3xl">🌊</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    {language === 'ar' ? 'الفجيرة' : 'Fujairah'}
                  </h3>
                  <p className="text-xs text-[#808080]">
                    {language === 'ar' ? 'بوابة الشرق' : 'Eastern Gateway'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* iter-272 — Task C removed: متابعة الاشتراكات والطلبات view deleted. */}

        {/* ============== صفحات الإمارات الفردية ============== */}
        {activeView.startsWith('emirate-') && (
          <EmiratePackagesView 
            emirateId={activeView.replace('emirate-', '')} 
            onBack={() => {
              // Feb 2026 — الرجوع لشبكة الإمارات المناسبة حسب mode
              if (emirateMode === 'tracking') setActiveView('gov-services-tracking');
              else setActiveView('gov-services-packages');
            }}
            language={language}
            activeSection={emirateSection}
            setActiveSection={setEmirateSection}
            mode={emirateMode}
          />
        )}

        {/* ============== التحكم بطلبات الطباعة وتخليص المستندات ============== */}
        {activeView === 'printing-documents' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <FileText className="h-6 w-6 text-cyan-500" />
              {language === 'ar' ? 'الطباعة وتخليص المستندات' : 'Printing & Document Clearance'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* تقديم طلب التأشيرة والهوية الإماراتية */}
              {canViewRoute('/control/cloudgm/visa-id') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                onClick={() => navigate('/control/cloudgm/visa-id')}
                className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer text-center"
                data-testid="ctrl-visa-id"
              >
                <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-emerald-500/20">
                  <FileText className="h-8 w-8 text-emerald-400" />
                </div>
                <h3 className="text-base font-bold text-white mb-1">
                  {language === 'ar' ? 'طلب التأشيرة والهوية' : 'Visa & ID Application'}
                </h3>
                <p className="text-xs text-[#71717A]">
                  {language === 'ar' ? 'إدارة طلبات التأشيرات والهوية' : 'Manage visa and ID applications'}
                </p>
              </motion.div>
              )}

              {/* توثيق المستندات */}
              {canViewRoute('/control/cloudgm/document-auth') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                whileHover={{ y: -5 }}
                onClick={() => navigate('/control/cloudgm/document-auth')}
                className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 cursor-pointer text-center"
                data-testid="ctrl-doc-auth"
              >
                <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-purple-500/20">
                  <Stamp className="h-8 w-8 text-purple-400" />
                </div>
                <h3 className="text-base font-bold text-white mb-1">
                  {language === 'ar' ? 'توثيق المستندات' : 'Document Authentication'}
                </h3>
                <p className="text-xs text-[#71717A]">
                  {language === 'ar' ? 'إدارة طلبات توثيق المستندات' : 'Manage document authentication'}
                </p>
              </motion.div>
              )}

              {/* خدمات المرور وترخيص المركبات والسائقين */}
              {canViewRoute('/control/cloudgm/traffic-services') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ y: -5 }}
                onClick={() => navigate('/control/cloudgm/traffic-services')}
                className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-orange-500/50 transition-all duration-300 cursor-pointer text-center"
                data-testid="ctrl-traffic"
              >
                <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border border-orange-500/20">
                  <Car className="h-8 w-8 text-orange-400" />
                </div>
                <h3 className="text-base font-bold text-white mb-1">
                  {language === 'ar' ? 'المرور والترخيص' : 'Traffic & Licensing'}
                </h3>
                <p className="text-xs text-[#71717A]">
                  {language === 'ar' ? 'خدمات المرور وترخيص المركبات' : 'Traffic and vehicle licensing'}
                </p>
              </motion.div>
              )}

              {/* Feb 2026 — كرت "التأشيرة السياحية" حُذف من هنا نهائياً لأنه مكرر
                  مع الصفحة الأعلى (`التحكم والمعلومات (منصة العملاء)`) — طلب المستخدم */}
            </div>
          </div>
        )}

        {/* صفحة تقديم طلب التأشيرة والهوية الإماراتية */}
        {activeView === 'visa-id' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <FileText className="h-6 w-6 text-emerald-500" />
              {language === 'ar' ? 'تقديم طلب التأشيرة والهوية الإماراتية' : 'Visa & Emirates ID Application'}
            </h2>

            <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
              <FileText className="h-16 w-16 mx-auto mb-4 opacity-30 text-emerald-500" />
              <p className="text-lg">{language === 'ar' ? 'لم يتم إضافة بيانات بعد' : 'No data added yet'}</p>
              <p className="text-sm mt-2 text-[#606060]">
                {language === 'ar' ? 'قريباً...' : 'Coming soon...'}
              </p>
            </div>
          </div>
        )}

        {/* Feb 2026 — صفحتا توثيق المستندات والمرور والترخيص أصبحتا صفحتين
            مستقلتين على /control/cloudgm/document-auth و /control/cloudgm/traffic-services
            (تعملان بنفس نمط visa-id). لم يعد هناك حاجة للـ activeView داخل هذه الصفحة. */}

        {/* صفحة تأشيرة سياحية لجميع الدول والجنسيات */}
        {activeView === 'tourist-visa' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Plane className="h-6 w-6 text-blue-500" />
              {language === 'ar' ? 'تأشيرة سياحية لجميع الدول والجنسيات' : 'Tourist Visa for All Countries'}
            </h2>

            <div className="text-center py-16 text-[#71717A] bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10">
              <Plane className="h-16 w-16 mx-auto mb-4 opacity-30 text-blue-500" />
              <p className="text-lg">{language === 'ar' ? 'لم يتم إضافة بيانات بعد' : 'No data added yet'}</p>
              <p className="text-sm mt-2 text-[#71717A]">
                {language === 'ar' ? 'قريباً...' : 'Coming soon...'}
              </p>
            </div>
          </div>
        )}

        {/* صفحة خدمات الترجمة القانونية */}
        {activeView === 'legal-translation' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Languages className="h-6 w-6 text-rose-400" />
              {language === 'ar' ? 'خدمات الترجمة القانونية' : 'Legal Translation Services'}
            </h2>

            <div className="text-center py-16 text-[#71717A] bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10">
              <Languages className="h-16 w-16 mx-auto mb-4 opacity-30 text-rose-400" />
              <p className="text-lg">{language === 'ar' ? 'لم يتم إضافة بيانات بعد' : 'No data added yet'}</p>
              <p className="text-sm mt-2 text-[#71717A]">
                {language === 'ar' ? 'قريباً...' : 'Coming soon...'}
              </p>
            </div>
          </div>
        )}
        </div>
      </main>
    </div>
  );
}
